import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import './ResetPasswordPage.css';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function validatePassword(password) {
  return {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>\[\]\\/~`_+=;'\-]/.test(password)
  };
}

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  const navigate = useNavigate();
  const query = useQuery();
  const email = query.get('email') || '';
  const token = query.get('token') || '';

  const passwordValid = validatePassword(password);
  const allValid = Object.values(passwordValid).every(Boolean) && password === confirmPassword;

  useEffect(() => {
    if (!email || !token) {
      setError('Invalid or expired reset link.');
      setTokenValid(false);
      setCheckingToken(false);
      return;
    }
    setCheckingToken(true);
    axios.post('/api/auth/verify-reset-token', { email, token })
      .then(res => {
        if (res.data.success) {
          setTokenValid(true);
          setError('');
        } else {
          setTokenValid(false);
          setError('Invalid or expired reset link.');
        }
      })
      .catch(() => {
        setTokenValid(false);
        setError('Invalid or expired reset link.');
      })
      .finally(() => setCheckingToken(false));
  }, [email, token]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    if (!password || !confirmPassword) {
      setError('Please enter and confirm your new password.');
      setIsLoading(false);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setIsLoading(false);
      return;
    }
    if (!allValid) {
      setError('Password does not meet the security requirements.');
      setIsLoading(false);
      return;
    }
    if (!email || !token) {
      setError('Invalid or expired reset link.');
      setIsLoading(false);
      return;
    }
    try {
      const response = await axios.post('/api/auth/reset-password', {
        email: email.toLowerCase().trim(),
        password,
        token
      });
      if (response.data.success) {
        setSuccess('Password reset successfully! Redirecting to login...');
        setPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-header">
          <div className="reset-password-logo" style={{ background: 'none', boxShadow: 'none', color: 'inherit', cursor: 'pointer' }} onClick={() => navigate('/login')}>
            <img src="/logo.svg" alt="VegaX Logo" style={{ width: 104, height: 104, display: 'block', objectFit: 'contain' }} />
          </div>
          <h1 className="reset-password-title">Reset Password</h1>
          <p className="reset-password-subtitle">Set a new password for your account</p>
        </div>
        <div className="back-to-login-section">
          <button type="button" className="back-to-login-button" onClick={handleBackToLogin}>
            ← Back to Login
          </button>
        </div>
        <div className="reset-password-form-container">
          {checkingToken ? (
            <div className="loading-container"><div className="loading-skeleton"></div></div>
          ) : tokenValid ? (
            <form onSubmit={handleResetPassword} className="reset-password-form">
              {error && <div className="error-alert"><span>⚠️</span>{error}</div>}
              {success && <div className="success-alert"><span>✅</span>{success}</div>}
              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <div className="password-input-container">
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="form-input"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      // 눈+빗금(숨김) SVG
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 2L18 18" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M10 4C5 4 2 10 2 10C2 10 4.5 15 10 15C12.1 15 13.9 14.2 15.2 13.1M17.5 10.5C17.5 10.5 15 4 10 4" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="10" cy="10" r="3" stroke="#888" strokeWidth="2"/>
                      </svg>
                    ) : (
                      // 눈(보임) SVG
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 10C2 10 5 4 10 4C15 4 18 10 18 10C18 10 15 16 10 16C5 16 2 10 2 10Z" stroke="#888" strokeWidth="2"/>
                        <circle cx="10" cy="10" r="3" stroke="#888" strokeWidth="2"/>
                      </svg>
                    )}
                  </button>
                </div>
                <ul className="password-validity-list">
                  <li className={passwordValid.length ? 'valid' : 'invalid'}>
                    {passwordValid.length ? '✔' : '✖'} 8 characters or more
                  </li>
                  <li className={passwordValid.upper ? 'valid' : 'invalid'}>
                    {passwordValid.upper ? '✔' : '✖'} At least one uppercase letter
                  </li>
                  <li className={passwordValid.lower ? 'valid' : 'invalid'}>
                    {passwordValid.lower ? '✔' : '✖'} At least one lowercase letter
                  </li>
                  <li className={passwordValid.number ? 'valid' : 'invalid'}>
                    {passwordValid.number ? '✔' : '✖'} At least one number
                  </li>
                  <li className={passwordValid.special ? 'valid' : 'invalid'}>
                    {passwordValid.special ? '✔' : '✖'} At least one special character
                  </li>
                </ul>
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">Confirm Password</label>
                <div className="password-input-container">
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="form-input"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      // 눈+빗금(숨김) SVG
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 2L18 18" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M10 4C5 4 2 10 2 10C2 10 4.5 15 10 15C12.1 15 13.9 14.2 15.2 13.1M17.5 10.5C17.5 10.5 15 4 10 4" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="10" cy="10" r="3" stroke="#888" strokeWidth="2"/>
                      </svg>
                    ) : (
                      // 눈(보임) SVG
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 10C2 10 5 4 10 4C15 4 18 10 18 10C18 10 15 16 10 16C5 16 2 10 2 10Z" stroke="#888" strokeWidth="2"/>
                        <circle cx="10" cy="10" r="3" stroke="#888" strokeWidth="2"/>
                      </svg>
                    )}
                  </button>
                </div>
                {confirmPassword && (
                  <div className={password === confirmPassword ? 'valid' : 'invalid'} style={{marginTop: 4}}>
                    {password === confirmPassword ? '✔ Passwords match' : '✖ Passwords do not match'}
                  </div>
                )}
              </div>
              <button type="submit" className="reset-password-button" disabled={isLoading || !allValid}>
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          ) : (
            <div className="error-alert"><span>⚠️</span>{error || 'Invalid or expired reset link.'}</div>
          )}
        </div>
        <div className="reset-password-help">
          <p>Password must meet all the requirements above.</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage; 